import { useCallback, useEffect, useMemo, useRef } from 'react'

import { listWallets, wagmiWallet, WalletInfo } from '@/config'
import useDebounce from '@/hooks/use-debounce'
import { isMobileDevice, StoredWalletData, WalletStorage } from '@/utils'
import { getWagmiWalletIcon, MAPPING_WAGMI_ID, WAGMI_CONNECTOR_ID } from '@/wallets/types/wagmi.type'

import { Connector, useAccount, useConnect, useDisconnect, useSendTransaction, useSignMessage } from 'wagmi'

function getConnectorWithId(
  connectors: readonly (Connector | undefined)[],
  id: string,
  options?: { shouldThrow: true }
): Connector | undefined {
  const connector = connectors.find((c) => c?.id === id)
  if (!connector && options?.shouldThrow) {
    throw new Error(`Expected connector ${id} missing from wagmi context.`)
  }
  return connector
}

function getInjectedConnectors(connectors: readonly Connector[]) {
  let isCoinbaseWalletBrowser = false
  const injectedConnectors = connectors.filter((c) => {
    // Special-case: Ignore coinbase eip6963-injected connector; coinbase connection is handled via the SDK connector.
    if (c.id === WAGMI_CONNECTOR_ID.COINBASE_RDNS) {
      if (isMobileDevice()) {
        isCoinbaseWalletBrowser = true
      }
      return false
    }

    return c.type === WAGMI_CONNECTOR_ID.INJECTED_CONNECTOR_ID && c.id !== WAGMI_CONNECTOR_ID.INJECTED_CONNECTOR_ID
  })

  // Special-case: Return deprecated window.ethereum connector when no eip6963 injectors are present.
  const fallbackInjector = getConnectorWithId(connectors, WAGMI_CONNECTOR_ID.INJECTED_CONNECTOR_ID, {
    shouldThrow: true,
  })
  if (!injectedConnectors.length && Boolean(window.ethereum)) {
    return { injectedConnectors: [fallbackInjector], isCoinbaseWalletBrowser }
  }

  return { injectedConnectors, isCoinbaseWalletBrowser }
}

export const useEvmAdapters = () => {
  const { connectors } = useConnect()

  return useMemo(() => {
    const { injectedConnectors: injectedConnectors, isCoinbaseWalletBrowser } = getInjectedConnectors(connectors)
    const coinbaseSdkConnector = getConnectorWithId(connectors, WAGMI_CONNECTOR_ID.COINBASE_SDK_CONNECTOR_ID)
    const walletConnectConnector = getConnectorWithId(connectors, WAGMI_CONNECTOR_ID.WALLET_CONNECT_CONNECTOR_ID)

    let orderedConnectors = []

    // // Special-case: Only display the injected connector for in-wallet browsers.
    if (isMobileDevice() && injectedConnectors.length === 1) {
      orderedConnectors = injectedConnectors
    }
    // Special-case: Only display the Coinbase connector in the Coinbase Wallet.
    else if (isCoinbaseWalletBrowser && coinbaseSdkConnector) {
      orderedConnectors = [coinbaseSdkConnector]
    } else {
      // Injected connectors should appear next in the list, as the user intentionally installed/uses them.
      orderedConnectors = [...injectedConnectors, walletConnectConnector, coinbaseSdkConnector]
    }

    orderedConnectors = orderedConnectors.filter(Boolean) as Connector[]

    return {
      adapters: orderedConnectors.map((connector) => {
        const isDetected = getConnectorWithId(injectedConnectors, connector.id)
        const walletConfig = listWallets.find((e) => e.key === MAPPING_WAGMI_ID[connector.id])
        const info: WalletInfo = {
          ...connector,
          adapter: connector,
          isDetected: () => !!isDetected,
          key: connector.id,
          priority: 0,
          wallet: wagmiWallet,
          icon: getWagmiWalletIcon(connector),
          priorityEvm: walletConfig?.priorityEvm ?? 10, // large enough
        }
        return info
      }),
    }
  }, [connectors])
}

// call at once place
export default function useWagmiWallet() {
  const data = useAccount()
  const { address, connector } = data
  const { signMessageAsync } = useSignMessage()
  const { sendTransactionAsync } = useSendTransaction()
  const { connectAsync } = useConnect({})

  const { disconnect } = useDisconnect()

  const disconnectAsync = useCallback(() => {
    return disconnect({ connector })
  }, [connector])

  // this is not a best practice, but with the current sol/btc wallet, this is the best way for now if we dont want to change btc/sol wallet interface as well
  useEffect(() => {
    wagmiWallet.syncData({
      signMessage: signMessageAsync,
      sendTransaction: sendTransactionAsync,
      connect: connectAsync,
      disconnect: disconnectAsync,
      data,
    })
  }, [signMessageAsync, sendTransactionAsync, connectAsync, address, data])
}

/**
 * Wagmi reconnect flow
 * 1. If connectStatus is reconnecting or connecting => Skip
 * 2. If connectStatus is connected => Wallet still active => Call onConnected
 * 3. If wallet is disconnected => Require login again => Don't call onConnected to prevent user change wallet account
 */
export const useReconnectEvm = (
  onConnected: (data: { adapter: Connector; network: string; data: StoredWalletData }) => void
) => {
  const { connector, status: connectStatus } = useAccount()
  const storeInfo = useRef(WalletStorage.getSaveEvmWallet())

  const hasInitialized = useRef(false)
  useEffect(() => {
    const shouldSkipReconnection =
      hasInitialized.current ||
      connectStatus === 'reconnecting' ||
      connectStatus === 'connecting' ||
      !storeInfo.current ||
      !connector
    if (shouldSkipReconnection) return

    hasInitialized.current = true
    if (connectStatus !== 'connected') return
    const [network, data] = storeInfo.current!
    onConnected({ adapter: connector, network, data })
    console.debug('reconnect wagmi', { connector, storeInfo: storeInfo.current })
  }, [connectStatus, connector])

  return useDebounce(connectStatus === 'reconnecting', 300)
}
